import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AppointmentMode } from '__generated__/globalTypes';
import { Dropdown, DropdownMenuItem } from 'Shared/Dropdown';
import {
  getGoogleCalendarLink,
  getOutlookCalendarLink,
  getOffice365CalendarLink,
  formatDate,
} from 'utils';
import { appointmentModeMap } from 'Book/shared';
import {
  AddToCalendarButton,
  AddToCalendarButtonLabel,
  ArrowIcon,
} from './styles';

export interface IAddToCalendarProps {
  counsellorName: string;
  dates: {
    beginsAt: Date;
    endsAt: Date;
    timezone?: string;
  };
  details?: {
    url?: string;
    id?: string;
    password?: string;
  };
  type?: AppointmentMode | string;
}

export const AddToCalendar = ({
  counsellorName,
  dates: { beginsAt, endsAt, timezone },
  details,
  type = AppointmentMode.PHONE,
}: IAddToCalendarProps) => {
  const { t } = useTranslation('book');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const toggleDropdownOpen = () => setIsDropdownOpen(prev => !prev);

  const isEAPAppointment = (
    apptType: AppointmentMode | string,
  ): apptType is AppointmentMode =>
    apptType === AppointmentMode.PHONE || apptType === AppointmentMode.VIDEO;

  type CalendarVendor = 'google' | 'outlook' | 'office365';

  const getCalendarEventDetails = (calendarVendor: CalendarVendor) => {
    const br = calendarVendor === 'google' ? '\n' : '<br/>';

    if (isEAPAppointment(type)) {
      const booked = t('calendar.eap.description', {
        counsellorName,
        date: formatDate(beginsAt),
      });
      const whatsNext = `<b>${t('calendar.eap.whats_next')}</b>`;
      const instructions = `${t(
        `calendar.eap.${appointmentModeMap[type]}_instructions`,
        {
          counsellorName,
        },
      )}`;
      const quietPlace = t('calendar.eap.quiet_place');

      return `${booked}${br}${br}${whatsNext}${br}<ul><li>${instructions}</li><li>${quietPlace}</li></ul>`;
    }

    const description = t('calendar.therapy.description', {
      counsellorName,
      date: formatDate(beginsAt, undefined, timezone),
      timeZone: timezone,
    });
    const video = t('calendar.therapy.videoLink', { callURL: details?.url });
    const id = t('calendar.therapy.id', { id: details?.id });
    const password = t('calendar.therapy.password', {
      password: details?.password,
    });

    return `${description}${br}${br}${video}${br}${id}${br}${password}`;
  };

  const getCalendarLink = (vendor: CalendarVendor) => {
    const getCalendarParams = (calendarVendor: CalendarVendor) => ({
      beginsAt,
      endsAt,
      subject: t('calendar.title', {
        counsellorName,
      }),
      details: getCalendarEventDetails(calendarVendor),
    });

    switch (vendor) {
      case 'outlook':
        return getOutlookCalendarLink(getCalendarParams('outlook'));
      case 'office365':
        return getOffice365CalendarLink(getCalendarParams('office365'));
      case 'google':
      default:
        return getGoogleCalendarLink(getCalendarParams('google'));
    }
  };

  return (
    <Dropdown
      isOpen={isDropdownOpen}
      closeDrawer={toggleDropdownOpen}
      triggerElement={
        <AddToCalendarButton
          label={
            <>
              <AddToCalendarButtonLabel>
                {t('calendar.addTo.cta')}
              </AddToCalendarButtonLabel>
              <ArrowIcon $isOpen={isDropdownOpen} />
            </>
          }
          type="button"
          onClick={toggleDropdownOpen}
        />
      }
      menuItems={(['google', 'outlook', 'office365'] as CalendarVendor[]).map(
        vendor => (
          <DropdownMenuItem
            key={vendor}
            label={t(`calendar.addTo.${vendor}.label`)}
            aria-label={t(`calendar.addTo.${vendor}.accessible`)}
            onClick={toggleDropdownOpen}
            href={getCalendarLink(vendor)}
            target={'_blank'}
            as={'a'}
          />
        ),
      )}
    />
  );
};
